package com.fantasytenis.web.screens.live

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.realtime.LiveScorePayload
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity
import com.fantasytenis.shared.domain.scoring.entity.MatchStatisticsEntity
import com.fantasytenis.shared.domain.tennis.entity.MatchEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.Skeleton
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.useMatchSubscription
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import com.fantasytenis.web.theme.textTransform
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// Console for logging
private val console: dynamic get() = window.asDynamic().console

/**
 * Detailed view for a single live match with real-time score updates.
 */
@Composable
fun LiveMatchDetailScreen(matchId: String) {
    val scope = rememberCoroutineScope()
    val realtimeState = useRealtime()
    val colors = Theme.colors

    // State
    var statistics by remember { mutableStateOf<List<MatchStatisticsEntity>>(emptyList()) }
    var matchEntity by remember { mutableStateOf<MatchEntity?>(null) }
    var matchLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // Observe live scores from SharedData
    val liveScores by SharedData.liveMatches.collectAsState()
    val liveScore = liveScores[matchId]

    // Debug: Log what's in SharedData
    LaunchedEffect(liveScores, matchId) {
        console.log("[DEBUG] LiveMatchDetailScreen - matchId: $matchId")
        console.log("[DEBUG] liveScores count: ${liveScores.size}")
        console.log("[DEBUG] liveScores keys: ${liveScores.keys.toList()}")
        console.log("[DEBUG] liveScore for this match: ${liveScore != null}")
        if (liveScore != null) {
            console.log("[DEBUG] liveScore player1: ${liveScore.player1.playerName}")
        }
    }

    // Subscribe to match updates
    useMatchSubscription(matchId) { scorePayload ->
        // The hook updates SharedData.liveMatches automatically
        console.log("[DEBUG] useMatchSubscription callback received scorePayload for: ${scorePayload.matchId}")
    }

    // Load match data and statistics
    LaunchedEffect(matchId) {
        matchLoading = true
        error = null

        // Try to find match entity from available endpoints
        // Try live matches first
        DependencyContainer.getLiveMatchesUseCase().fold(
            onSuccess = { matches ->
                matchEntity = matches.find { it.id == matchId }
            },
            onFailure = { }
        )

        // If not found, try upcoming
        if (matchEntity == null) {
            DependencyContainer.getUpcomingMatchesUseCase(limit = 50).fold(
                onSuccess = { matches ->
                    matchEntity = matches.find { it.id == matchId }
                },
                onFailure = { }
            )
        }

        // If still not found, try recently completed
        if (matchEntity == null) {
            DependencyContainer.getRecentlyCompletedMatchesUseCase().fold(
                onSuccess = { matches ->
                    matchEntity = matches.find { it.id == matchId }
                },
                onFailure = { }
            )
        }

        matchLoading = false

        // Also load statistics
        DependencyContainer.getMatchStatisticsUseCase(matchId).fold(
            onSuccess = { stats ->
                statistics = stats
            },
            onFailure = { e ->
                // Statistics error is not critical - we can show match without them
                if (matchEntity == null && liveScore == null) {
                    error = e.message ?: "Failed to load match"
                }
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(colors.primary)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Button(
                    text = "Back",
                    onClick = { NavigationState.navigate(Route.Matches) },
                    variant = "secondary"
                )
                H2({
                    style {
                        color(colors.textOnPrimary)
                        fontSize(24.px)
                        fontWeight(700)
                    }
                }) {
                    Text("Match Details")
                }
            }

            // Connection indicator
            ConnectionIndicator(realtimeState.isConnected, colors)
        }

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(800.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                when {
                    matchLoading && liveScore == null -> LoadingState(colors)
                    error != null && liveScore == null && matchEntity == null -> ErrorState(error!!, colors) {
                        scope.launch {
                            matchLoading = true
                            error = null
                            DependencyContainer.getLiveMatchesUseCase().fold(
                                onSuccess = { matches ->
                                    matchEntity = matches.find { it.id == matchId }
                                    matchLoading = false
                                },
                                onFailure = { e ->
                                    error = e.message
                                    matchLoading = false
                                }
                            )
                        }
                    }
                    else -> MatchContent(liveScore, matchEntity, statistics, colors)
                }
            }
        }
    }
}

@Composable
private fun ConnectionIndicator(isConnected: Boolean, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            backgroundColor(rgba(255, 255, 255, 0.1))
            padding(8.px, 12.px)
            borderRadius(20.px)
        }
    }) {
        Span({
            style {
                width(10.px)
                height(10.px)
                borderRadius(50.percent)
                backgroundColor(if (isConnected) colors.success else colors.error)
                if (isConnected) {
                    property("animation", "pulse 2s infinite")
                }
            }
        })
        Span({
            style {
                fontSize(12.px)
                color(colors.textOnPrimary)
                fontWeight(500)
            }
        }) {
            Text(if (isConnected) "Live" else "Connecting...")
        }
    }
}

@Composable
private fun MatchContent(
    liveScore: LiveScoreEntity?,
    matchEntity: MatchEntity?,
    statistics: List<MatchStatisticsEntity>,
    colors: ThemeColors
) {
    // Main scoreboard
    ScoreboardCard(liveScore, matchEntity, colors)

    // Statistics section
    if (statistics.isNotEmpty()) {
        Div({ style { height(24.px) } })
        StatisticsSection(statistics, liveScore, colors)
    }

    // Fantasy Points section
    liveScore?.let { score ->
        if (score.fantasyPoints.isNotEmpty()) {
            Div({ style { height(24.px) } })
            FantasyPointsSection(score, colors)
        }
    }
}

@Composable
private fun ScoreboardCard(liveScore: LiveScoreEntity?, matchEntity: MatchEntity?, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(16.px)
            padding(24.px)
            property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.1)")
        }
    }) {
        when {
            liveScore != null -> {
                // Live badge centered
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        marginBottom(20.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            backgroundColor(rgba(244, 67, 54, 0.1))
                            padding(6.px, 16.px)
                            borderRadius(20.px)
                        }
                    }) {
                        Span({
                            style {
                                width(10.px)
                                height(10.px)
                                borderRadius(50.percent)
                                backgroundColor(colors.error)
                                property("animation", "pulse 1.5s infinite")
                            }
                        })
                        Span({
                            style {
                                fontSize(13.px)
                                fontWeight(700)
                                color(colors.error)
                                textTransform("uppercase")
                            }
                        }) {
                            Text("LIVE")
                        }
                    }
                }

                // Player 1
                ScoreboardPlayerRow(
                    playerName = liveScore.player1.playerName,
                    sets = liveScore.player1.sets,
                    games = liveScore.player1.games,
                    points = liveScore.player1.points,
                    tiebreakPoints = liveScore.player1.tiebreakPoints,
                    isServing = liveScore.serving == "player1",
                    colors = colors
                )

                // Divider
                Div({
                    style {
                        height(1.px)
                        backgroundColor(colors.border)
                        margin(16.px, 0.px)
                    }
                })

                // Player 2
                ScoreboardPlayerRow(
                    playerName = liveScore.player2.playerName,
                    sets = liveScore.player2.sets,
                    games = liveScore.player2.games,
                    points = liveScore.player2.points,
                    tiebreakPoints = liveScore.player2.tiebreakPoints,
                    isServing = liveScore.serving == "player2",
                    colors = colors
                )

                // Set/Game info
                Div({
                    style {
                        marginTop(20.px)
                        textAlign("center")
                        fontSize(13.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Set ${liveScore.currentSet}, Game ${liveScore.currentGame}")
                }
            }
            matchEntity != null -> {
                // Static match display from API data
                // Status badge
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        marginBottom(20.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(13.px)
                            fontWeight(600)
                            color(colors.textSecondary)
                            backgroundColor(colors.surfaceVariant)
                            padding(6.px, 16.px)
                            borderRadius(20.px)
                        }
                    }) {
                        Text(matchEntity.status.uppercase())
                    }
                }

                // Player 1 row
                StaticPlayerRow(
                    playerName = matchEntity.player1Name ?: "TBD",
                    colors = colors
                )

                // Divider
                Div({
                    style {
                        height(1.px)
                        backgroundColor(colors.border)
                        margin(16.px, 0.px)
                    }
                })

                // Player 2 row
                StaticPlayerRow(
                    playerName = matchEntity.player2Name ?: "TBD",
                    colors = colors
                )

                // Show period scores if available
                if (matchEntity.periodScores.isNotEmpty()) {
                    Div({
                        style {
                            marginTop(20.px)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            gap(16.px)
                        }
                    }) {
                        matchEntity.periodScores.forEach { period ->
                            Div({
                                style {
                                    textAlign("center")
                                    padding(8.px, 12.px)
                                    backgroundColor(colors.surfaceVariant)
                                    borderRadius(8.px)
                                }
                            }) {
                                Div({
                                    style {
                                        fontSize(12.px)
                                        color(colors.textSecondary)
                                        marginBottom(4.px)
                                    }
                                }) {
                                    Text("Set ${period.setNumber}")
                                }
                                Div({
                                    style {
                                        fontSize(16.px)
                                        fontWeight(600)
                                        color(colors.textPrimary)
                                    }
                                }) {
                                    Text("${period.homeScore}-${period.awayScore}")
                                }
                            }
                        }
                    }
                }

                // Show competition/round info if available
                if (matchEntity.competition != null || matchEntity.round != null) {
                    Div({
                        style {
                            marginTop(16.px)
                            textAlign("center")
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        val info = listOfNotNull(matchEntity.competition, matchEntity.round).joinToString(" - ")
                        Text(info)
                    }
                }
            }
            else -> {
                // No data available
                Div({
                    style {
                        textAlign("center")
                        padding(32.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Match not found")
                }
            }
        }
    }
}

@Composable
private fun StaticPlayerRow(playerName: String, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        Span({
            style {
                fontSize(18.px)
                fontWeight(500)
                color(colors.textPrimary)
            }
        }) {
            Text(playerName)
        }
    }
}

@Composable
private fun ScoreboardPlayerRow(
    playerName: String,
    sets: List<Int>,
    games: Int,
    points: String,
    tiebreakPoints: Int?,
    isServing: Boolean,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        // Player name with serving indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            if (isServing) {
                Div({
                    style {
                        width(12.px)
                        height(12.px)
                        borderRadius(50.percent)
                        backgroundColor(colors.secondary)
                    }
                })
            } else {
                Div({ style { width(12.px) } })
            }
            Span({
                style {
                    fontSize(18.px)
                    fontWeight(if (isServing) 700 else 500)
                    color(colors.textPrimary)
                }
            }) {
                Text(playerName)
            }
        }

        // Scores
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            // Set scores
            sets.forEachIndexed { index, setScore ->
                Span({
                    style {
                        width(32.px)
                        textAlign("center")
                        fontSize(20.px)
                        fontWeight(700)
                        color(colors.textPrimary)
                    }
                }) {
                    Text(setScore.toString())
                }
            }

            // Current games in set
            Span({
                style {
                    width(36.px)
                    textAlign("center")
                    fontSize(18.px)
                    fontWeight(600)
                    color(colors.primary)
                    backgroundColor(colors.primaryLight)
                    padding(4.px, 8.px)
                    borderRadius(6.px)
                }
            }) {
                Text(games.toString())
            }

            // Current points / tiebreak
            Span({
                style {
                    width(40.px)
                    textAlign("center")
                    fontSize(16.px)
                    fontWeight(500)
                    color(colors.textSecondary)
                }
            }) {
                Text(tiebreakPoints?.toString() ?: points)
            }
        }
    }
}

@Composable
private fun StatisticsSection(
    statistics: List<MatchStatisticsEntity>,
    liveScore: LiveScoreEntity?,
    colors: ThemeColors
) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(16.px)
            padding(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                h3Style()
                marginBottom(20.px)
                color(colors.textPrimary)
            }
        }) {
            Text("Match Statistics")
        }

        // Get stats for each player if available
        val player1Stats = statistics.firstOrNull()
        val player2Stats = statistics.getOrNull(1)

        if (player1Stats != null) {
            StatRow("Aces", player1Stats.aces, player2Stats?.aces ?: 0, colors)
            StatRow("Double Faults", player1Stats.doubleFaults, player2Stats?.doubleFaults ?: 0, colors)
            StatRow(
                "Winners",
                player1Stats.forehandWinners + player1Stats.backhandWinners,
                (player2Stats?.forehandWinners ?: 0) + (player2Stats?.backhandWinners ?: 0),
                colors
            )
            StatRow(
                "Unforced Errors",
                player1Stats.forehandErrors + player1Stats.backhandErrors,
                (player2Stats?.forehandErrors ?: 0) + (player2Stats?.backhandErrors ?: 0),
                colors
            )
            StatRow("Break Points Converted", player1Stats.breakPointsConverted, player2Stats?.breakPointsConverted ?: 0, colors)
            StatRow("Break Points Saved", player1Stats.breakPointsSaved, player2Stats?.breakPointsSaved ?: 0, colors)
        } else {
            Div({
                style {
                    textAlign("center")
                    padding(24.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("Statistics will be available once the match progresses")
            }
        }
    }
}

@Composable
private fun StatRow(label: String, value1: Int, value2: Int, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(12.px, 0.px)
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) {
        // Player 1 value
        Span({
            style {
                fontSize(16.px)
                fontWeight(if (value1 > value2) 700 else 400)
                color(if (value1 > value2) colors.primary else colors.textPrimary)
                width(60.px)
                textAlign("left")
            }
        }) {
            Text(value1.toString())
        }

        // Label
        Span({
            style {
                fontSize(14.px)
                color(colors.textSecondary)
                textAlign("center")
                flex(1)
            }
        }) {
            Text(label)
        }

        // Player 2 value
        Span({
            style {
                fontSize(16.px)
                fontWeight(if (value2 > value1) 700 else 400)
                color(if (value2 > value1) colors.primary else colors.textPrimary)
                width(60.px)
                textAlign("right")
            }
        }) {
            Text(value2.toString())
        }
    }
}

@Composable
private fun FantasyPointsSection(liveScore: LiveScoreEntity, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(16.px)
            padding(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                h3Style()
                marginBottom(20.px)
                color(colors.textPrimary)
            }
        }) {
            Text("Fantasy Points")
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "1fr 1fr")
                gap(16.px)
            }
        }) {
            // Player 1 fantasy points
            FantasyPointsCard(
                playerName = liveScore.player1.playerName,
                points = liveScore.fantasyPoints[liveScore.player1.playerId] ?: 0.0,
                colors = colors
            )

            // Player 2 fantasy points
            FantasyPointsCard(
                playerName = liveScore.player2.playerName,
                points = liveScore.fantasyPoints[liveScore.player2.playerId] ?: 0.0,
                colors = colors
            )
        }
    }
}

@Composable
private fun FantasyPointsCard(playerName: String, points: Double, colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.primaryLight)
            borderRadius(12.px)
            padding(20.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(14.px)
                color(colors.primary)
                marginBottom(8.px)
                fontWeight(500)
            }
        }) {
            Text(playerName)
        }
        Div({
            style {
                fontSize(32.px)
                fontWeight(700)
                color(colors.primary)
            }
        }) {
            Text("${points.toInt()}")
        }
        Div({
            style {
                fontSize(12.px)
                color(colors.textSecondary)
                marginTop(4.px)
            }
        }) {
            Text("Fantasy Points")
        }
    }
}

@Composable
private fun LoadingState(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(24.px)
        }
    }) {
        // Skeleton scoreboard
        Div({
            style {
                backgroundColor(colors.surface)
                borderRadius(16.px)
                padding(24.px)
            }
        }) {
            Skeleton(width = 80.px, height = 28.px, borderRadius = 20.px)
            Div({ style { height(20.px) } })

            repeat(2) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(16.px)
                    }
                }) {
                    Skeleton(width = 150.px, height = 20.px)
                    Skeleton(width = 120.px, height = 24.px)
                }
            }
        }

        // Skeleton statistics
        Div({
            style {
                backgroundColor(colors.surface)
                borderRadius(16.px)
                padding(24.px)
            }
        }) {
            Skeleton(width = 160.px, height = 24.px)
            Div({ style { height(20.px) } })

            repeat(5) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        padding(12.px, 0.px)
                    }
                }) {
                    Skeleton(width = 40.px, height = 16.px)
                    Skeleton(width = 100.px, height = 14.px)
                    Skeleton(width = 40.px, height = 16.px)
                }
            }
        }
    }
}

@Composable
private fun ErrorState(message: String, colors: ThemeColors, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
            backgroundColor(colors.surface)
            borderRadius(16.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                marginBottom(16.px)
            }
        }) {
            Text("\u26A0\uFE0F")
        }

        P({
            style {
                color(colors.error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }

        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

// Helper function for rgba color
private fun rgba(r: Int, g: Int, b: Int, a: Double): CSSColorValue {
    return Color("rgba($r, $g, $b, $a)")
}
